import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import { OrthographicCamera, PerspectiveCamera } from 'three';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { FrustumViewerMessage } from 'app/modules/wallforming/components/frustum-viewer/types/frustumViewerMessage';
import type { CameraHelperData } from 'app/modules/wallforming/components/scenes/objects/helpers/types/cameraHelperData';

export const SYNC_THROTTLE_MS = 50; // ~20 fps

/**
 * useFrustumViewerSyncHandler — R3F hook to synchronize main scene camera state to the frustum viewer scene via postMessage.
 *
 * Must be used inside a Canvas component.
 */
export const useFrustumViewerSyncHandler = () => {
    const cameraFrustumViewerRef = usePlacementStore((state) => state.cameraFrustumViewerRef);

    const lastSyncTimeRef = useRef<number>(0);
    const camera = useThree((state) => state.camera);

    useFrame(({ clock }) => {
        const tab = cameraFrustumViewerRef.current;
        if (!tab || tab.closed) {
            return;
        }

        // Throttle camera updates
        const nowMs = clock.getElapsedTime() * 1000;
        if (nowMs - lastSyncTimeRef.current < SYNC_THROTTLE_MS) {
            return;
        }
        lastSyncTimeRef.current = nowMs;

        let cameraHelperData: CameraHelperData;
        if (camera instanceof OrthographicCamera) {
            cameraHelperData = {
                projectionType: CameraProjectionType.ORTHOGRAPHIC,
                near: camera.near,
                far: camera.far,
                left: camera.left,
                right: camera.right,
                top: camera.top,
                bottom: camera.bottom,
                zoom: camera.zoom,
                projectionMatrix: camera.projectionMatrix.toArray(),
                matrixWorld: camera.matrixWorld.toArray()
            };
        } else {
            const perspectiveCamera = camera as PerspectiveCamera;
            const effectiveFov = perspectiveCamera.getEffectiveFOV();
            const halfHeight = Math.tan((effectiveFov * Math.PI) / 360) * perspectiveCamera.near;
            const halfWidth = halfHeight * perspectiveCamera.aspect;
            cameraHelperData = {
                projectionType: CameraProjectionType.PERSPECTIVE,
                near: perspectiveCamera.near,
                far: perspectiveCamera.far,
                left: -halfWidth,
                right: halfWidth,
                top: halfHeight,
                bottom: -halfHeight,
                fov: perspectiveCamera.fov,
                aspect: perspectiveCamera.aspect,
                zoom: perspectiveCamera.zoom,
                projectionMatrix: perspectiveCamera.projectionMatrix.toArray(),
                matrixWorld: perspectiveCamera.matrixWorld.toArray()
            };
        }

        // Send camera update
        const updateMessage: FrustumViewerMessage = {
            type: 'CAMERA_FRUSTUM_UPDATE',
            camera: cameraHelperData
        };
        tab.postMessage(updateMessage, '*');
    });
};
